import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { connect } from 'react-redux';
import { arrayIncludes } from '@material-ui/pickers/_helpers/utils';
import {
    updateRouter, addRouter, getRouter, getRouters, getAvailableSimcards
} from '../../../stores/actions/installerActions';
import { getSystemsNodesShort } from '../../../stores/actions/logsActions';

interface RouterEditDialogProps {
    visible: boolean;
    initialRouter?: any;        // pass rowData from editRouter(), null/undefined for new
    onHide: () => void;
    // from Redux
    systemsNodesShort: any[];
    mobileView: boolean;
    dbRouters: any[];
    freeSimcards: any[];
    currentRouter: any;
    updateRouterSuccess: boolean;
    updateRouterFail: boolean;
    createRouterSuccess: boolean;
    createRouterFail: boolean;
    updateRouter: (router) => void;
    addRouter: (router) => void;
    getRouter: (routerId) => void;
    getRouters: () => void;
    getAvailableSimcards: () => void;
    getSystemsNodesShort: () => void;
}

function formatDateRemoveTime(date) {
    return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
}

const emptyRouter = {
    ID: null,
    systemId: null, systemName: null,
    type: 'other', firmware: null, serialNumber: null,
    purchaseDate: formatDateRemoveTime(new Date()),
    owner: 'WISENE', simcardId: null, simcardName: null,
    notes: null, remarks: null, updatedAt: null,
    supplier: 'WISENE',
    installationDate: formatDateRemoveTime(new Date()),
    upsType: null, upsSerialNumber: null,
    upsInstallationDate: formatDateRemoveTime(new Date()),
    routerImageData: null, routerImageDataMime: null,
    upsImageData: null, upsImageDataMime: null,
    antennaImageData: null, antennaImageDataMime: null,
    routerConnectionImageData: null, routerConnectionImageDataMime: null,
    modemRestarter: false,
    nodeId: null, nodeName: null,
    previousNodeId: null, previousNodeName: null,
    previousSimcardId: null, previousSimcardName: null,
    previousSystemName: null
};

const owners = ['customer', 'WISENE'];
const suppliers = ['customer', 'WISENE'];
const routerTypes = [
    'MOXA OnCell G3151-HSPA', 'MOXA OnCell 3120-LTE-1 HW Rev. 1.0.0',
    'MOXA OnCell 3120-LTE-1 HW Rev. 2.0.0', 'ADVANTECH ICR-2431',
    'ADVANTECH ICR-2031', 'other', 'FRITZ!Box 6890 LTE'
];
const upsTypes = [
    'Router UPS-15', 'Router UPS-30', 'Router UPS-30-PoE',
    'Router UPS-15-PoE', 'ZAS-24.1.1'
];

const RouterEditDialog = ({
    visible, initialRouter, onHide,
    systemsNodesShort, mobileView, dbRouters, freeSimcards, currentRouter,
    updateRouterSuccess, updateRouter, addRouter, getRouter,
    getRouters, getAvailableSimcards, getSystemsNodesShort
}: RouterEditDialogProps) => {

    const [router, setRouter] = useState(emptyRouter);
    const fileUploadRefs = useRef<{ [key: string]: any }>({});
    const [uploadError, setUploadError] = useState<{ source: string; message: string } | null>(null);

    // Seed form when dialog opens
    useEffect(() => {
        if (visible) {
            setRouter(initialRouter ? { ...emptyRouter, ...initialRouter } : emptyRouter);
            setUploadError(null);
        }
    }, [visible, initialRouter]);

    useEffect(() => {
        if (visible) {
            getSystemsNodesShort();
            getRouters();
            getAvailableSimcards();
        }
    }, [visible]);

    // Fetch images when editing
    useEffect(() => {
        if (visible && router.ID) {
            getRouter(router.ID);
        }
    }, [visible, router.ID]);

    useEffect(() => {
        if (currentRouter) {
            setRouter(prev => ({
                ...prev,
                routerImageData: currentRouter.routerImageData,
                routerImageDataMime: currentRouter.routerImageDataMime,
                upsImageData: currentRouter.upsImageData,
                upsImageDataMime: currentRouter.upsImageDataMime,
                antennaImageData: currentRouter.antennaImageData,
                antennaImageDataMime: currentRouter.antennaImageDataMime,
                routerConnectionImageData: currentRouter.routerConnectionImageData,
                routerConnectionImageDataMime: currentRouter.routerConnectionImageDataMime,
                previousNodeId: prev.nodeId,
                previousNodeName: prev.nodeName,
                previousSimcardId: prev.simcardId,
                previousSimcardName: prev.simcardName,
                previousSystemName: prev.systemName,
            }));
        }
    }, [currentRouter]);

    // Close on success
    useEffect(() => {
        if (updateRouterSuccess) {
            setTimeout(() => handleHide(), 50);
        }
    }, [updateRouterSuccess]);

    const handleHide = () => {
        setRouter(emptyRouter);
        onHide();
    };

    const saveRouter = () => {
        if (!router.serialNumber) return;
        router.ID ? updateRouter(router) : addRouter(router);
    };

    const onInputChange = (e, name) => {
        let val = e.value !== undefined ? e.value : e.target?.value ?? null;

        if (val === '') val = null;

        if (name === 'nodeId' && val) {
            const sn = systemsNodesShort.find(item => item.nodeId === val);
            setRouter(prev => ({
                ...prev,
                nodeId: val, nodeName: sn.nodeName,
                systemId: sn.systemId, systemName: sn.systemName
            }));
        } else if (name === 'nodeId' && !val) {
            setRouter(prev => ({
                ...prev,
                nodeId: null, nodeName: null, systemId: null, systemName: null
            }));
        } else if (name === 'simcardId' && val) {
            const simcard = simcards.find(item => item.ID === val);
            setRouter(prev => ({ ...prev, simcardId: val, simcardName: simcard.label }));
        } else if (name === 'simcardId' && !val) {
            setRouter(prev => ({ ...prev, simcardId: null, simcardName: null }));
        } else if (name === 'remarks' && val) {
            setRouter(prev => ({ ...prev, [name]: val.length > 50 ? val.slice(0, 50) : val }));
        } else if (val && ['purchaseDate', 'installationDate', 'upsInstallationDate'].includes(name)) {
            setRouter(prev => ({ ...prev, [name]: formatDateRemoveTime(val) }));
        } else {
            setRouter(prev => ({ ...prev, [name]: val }));
        }
    };

    const handleFileSelect = (e, type) => {
        const file = e.files[0];
        if (!file) return;
        setUploadError(null);
        const reader = new FileReader();
        const fieldMap = {
            router: ['routerImageData', 'routerImageDataMime'],
            ups: ['upsImageData', 'upsImageDataMime'],
            antenna: ['antennaImageData', 'antennaImageDataMime'],
            router_connection: ['routerConnectionImageData', 'routerConnectionImageDataMime'],
        };
        const [dataField, mimeField] = fieldMap[type];
        reader.onloadend = () => {
            setRouter(prev => ({
                ...prev,
                [dataField]: reader.result.split(",")[1],
                [mimeField]: file.type
            }));
            fileUploadRefs.current[type]?.clear();
        };
        reader.onerror = () => {
            setUploadError({ source: type, message: `Could not read "${file.name}". Please try again.` });
            fileUploadRefs.current[type]?.clear();
        };
        reader.readAsDataURL(file);
    };

    // Derived lists
    let nodeNames = [];
    if (systemsNodesShort && dbRouters?.length) {
        const takenNodeIds = dbRouters.map(el => el.nodeId);
        const freeNodes = systemsNodesShort
            .filter(v => !arrayIncludes(takenNodeIds, v.nodeId) && v.deviceRole === 'gateway')
            .sort((a, b) => a.systemName.localeCompare(b.systemName));
        nodeNames = freeNodes.map(item => ({ nodeId: item.nodeId, nodeName: `${item.systemName} ${item.nodeName}` }));
        if (router.ID && router.nodeId) {
            nodeNames = [...nodeNames, { nodeId: router.nodeId, nodeName: `${router.systemName} ${router.nodeName}` }];
        } else if (router.ID && router.previousNodeId) {
            nodeNames = [...nodeNames, { nodeId: router.previousNodeId, nodeName: `${router.previousSystemName} ${router.previousNodeName}` }];
        }
    }

    const simcardLabel = (item) => [item.ip, item.telephone].filter(Boolean).join(' / ');

    let simcards = [];
    if (dbRouters && freeSimcards !== undefined) {
        if (router.ID && router.simcardId) {
            simcards = [...freeSimcards, { ID: router.simcardId, ip: router.simcardName, telephone: null }];
        } else if (router.ID && router.previousSimcardId) {
            simcards = [...freeSimcards, { ID: router.previousSimcardId, ip: router.previousSimcardName, telephone: null }];
        } else {
            simcards = [...freeSimcards];
        }
        simcards = simcards.map(item => ({ ...item, label: simcardLabel(item) }));
    }

    // Reusable image field
    const imageField = (label, dataField, mimeField, uploadType) => (
        <div className="field flex-col mr-3">
            <label className="mb-3 font-bold flex">{label}</label>
            {router[dataField] ? (
                <div className="relative w-32 border rounded shadow-sm flex items-center justify-center overflow-hidden"
                     style={{ height: "200px", flexDirection: "column", textAlign: "center" }}>
                    <Image
                        src={`data:${router[mimeField]};base64,${router[dataField]}`}
                        alt={label} height="200" preview
                        style={{ objectFit: 'contain' }}
                        className="border rounded shadow-sm"
                    />
                    <button type="button"
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition"
                        onClick={() => {
                            setRouter(prev => ({ ...prev, [dataField]: null, [mimeField]: null }));
                            fileUploadRefs.current[uploadType]?.clear();
                        }}>
                        <i className="pi pi-times text-sm"></i>
                    </button>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center text-gray-400"
                     style={{ height: "200px", flexDirection: "column", textAlign: "center" }}>
                    <i className="pi pi-image text-6xl mt-5" />
                    <span className="text-xs mt-1">No image</span>
                </div>
            )}
            <div className="items-center gap-4 mt-3">
                <FileUpload ref={(el) => { fileUploadRefs.current[uploadType] = el; }} mode="basic" accept="image/*" maxFileSize={5000000}
                    customUpload auto chooseLabel="Select Image"
                    onSelect={(e) => handleFileSelect(e, uploadType)}
                    onValidationFail={(file) => setUploadError({ source: uploadType, message: `"${file.name}" is too large - maximum size is 5 MB.` })} />
                {uploadError?.source === uploadType && (
                    <small className="p-error block mt-1">{uploadError.message}</small>
                )}
            </div>
        </div>
    );

    const dialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={handleHide} />
            <Button label="Save" icon="pi pi-check" onClick={saveRouter} />
        </React.Fragment>
    );

    const f = mobileView ? "field" : "field col";

    return (
        <Dialog
            visible={visible}
            style={{ width: mobileView ? '100%' : '90rem' }}
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}
            modal className="p-fluid" footer={dialogFooter} onHide={handleHide}
            header={router.ID ? `Edit router #${router.ID}` : 'Add new router'}
        >
            <div className="formgrid grid">
                <div className="field col">
                    <label className="mb-3 font-bold">Node</label>
                    <Dropdown value={router.nodeId} onChange={(e) => onInputChange(e, 'nodeId')}
                        options={nodeNames} optionLabel="nodeName" optionValue="nodeId"
                        placeholder="Select gateway" className="md:w-28rem" filter showClear filterPlaceholder="Type to search..." />
                </div>
                <div className="field col">
                    <label className="mb-3 font-bold">SIM card</label>
                    <Dropdown value={router.simcardId} onChange={(e) => onInputChange(e, 'simcardId')}
                        options={simcards} optionLabel="label" optionValue="ID"
                        placeholder="Select SIM card" className="md:w-28rem" filter filterBy="ip,telephone"
                        showClear filterPlaceholder="Type to search..." />
                </div>
            </div>
            <div className="formgrid grid">
                <div className={f}>
                    <label className="mb-3 font-bold">Serial number</label>
                    <InputText value={router.serialNumber} maxLength={128} invalid={!router.serialNumber} onChange={(e) => onInputChange(e, 'serialNumber')} />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">Firmware</label>
                    <InputText value={router.firmware} maxLength={128} onChange={(e) => onInputChange(e, 'firmware')} />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">Type</label>
                    <Dropdown value={router.type} onChange={(e) => onInputChange(e, 'type')} options={routerTypes}
                        optionLabel="type" filter placeholder={router.type ?? "Select type"} invalid={!router.type} />
                </div>
            </div>
            <div className="formgrid grid">
                <div className={f}>
                    <label className="mb-3 font-bold">UPS Serial number</label>
                    <InputText value={router.upsSerialNumber} maxLength={128} onChange={(e) => onInputChange(e, 'upsSerialNumber')} />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">UPS Installation date</label>
                    <Calendar value={new Date(router.upsInstallationDate)} onChange={(e) => onInputChange(e, 'upsInstallationDate')} dateFormat="yy-mm-dd" />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">UPS type</label>
                    <Dropdown value={router.upsType} onChange={(e) => onInputChange(e, 'upsType')} options={upsTypes}
                        optionLabel="upsType" filter placeholder={router.upsType ?? "Select UPS type"} showClear />
                </div>
            </div>
            <div className="formgrid grid">
                <div className={f}>
                    <label className="mb-3 font-bold">Owner</label>
                    <Dropdown value={router.owner} onChange={(e) => onInputChange(e, 'owner')} options={owners}
                        optionLabel="owner" filter placeholder={router.owner ?? "Select owner"} invalid={!router.owner} />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">Supplier</label>
                    <Dropdown value={router.supplier} onChange={(e) => onInputChange(e, 'supplier')} options={suppliers}
                        optionLabel="supplier" filter placeholder={router.supplier ?? "Select supplier"} />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">Purchase date</label>
                    <Calendar value={new Date(router.purchaseDate)} onChange={(e) => onInputChange(e, 'purchaseDate')} dateFormat="yy-mm-dd" />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">Installation date</label>
                    <Calendar value={new Date(router.installationDate)} onChange={(e) => onInputChange(e, 'installationDate')} dateFormat="yy-mm-dd" />
                </div>
            </div>
            <div className="formgrid grid">
                <div className={f}>
                    <label className="mb-3 font-bold">Notes</label>
                    <ReactQuill theme="snow" value={router.notes || ''} onChange={(value) => onInputChange({ value }, 'notes')} />
                </div>
                <div className={f}>
                    <label className="mb-3 font-bold">Remarks</label>
                    <ReactQuill theme="snow" value={router.remarks || ''} onChange={(value) => onInputChange({ value }, 'remarks')} />
                </div>
            </div>
            <div className="field-checkbox mb-3">
                <Checkbox inputId="modemRestarter" checked={!!router.modemRestarter}
                    onChange={(e) => onInputChange({ value: e.checked }, 'modemRestarter')} />
                <label htmlFor="modemRestarter" className="font-bold">Modem restarter</label>
            </div>
            <div className="formgrid grid">
                {imageField('Router Image', 'routerImageData', 'routerImageDataMime', 'router')}
                {imageField('UPS Image', 'upsImageData', 'upsImageDataMime', 'ups')}
                {imageField('Antenna Image', 'antennaImageData', 'antennaImageDataMime', 'antenna')}
                {imageField('Router Connection Image', 'routerConnectionImageData', 'routerConnectionImageDataMime', 'router_connection')}
            </div>
        </Dialog>
    );
};

const mapStateToProps = (state) => ({
    systemsNodesShort: state.dashboard.systemsNodesShort,
    mobileView: state.dashboard.mobileView,
    dbRouters: state.dashboard.routers,
    freeSimcards: state.dashboard.freeSimcards,
    currentRouter: state.dashboard.currentRouter,
    updateRouterSuccess: state.dashboard.updateRouterSuccess,
    updateRouterFail: state.dashboard.updateRouterFail,
    createRouterSuccess: state.dashboard.createRouterSuccess,
    createRouterFail: state.dashboard.createRouterFail,
});

const actions = {
    updateRouter, addRouter, getRouter, getRouters, getAvailableSimcards, getSystemsNodesShort
};

export default connect(mapStateToProps, actions)(RouterEditDialog);